/**
 * Switch Component - Toggle switch
 *
 * Variants:
 * - default: Primary color
 * - success: Success color
 *
 * Sizes:
 * - sm: Small switch
 * - md: Medium switch (default)
 * - lg: Large switch
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const switchVariants = cva(
  'peer relative inline-flex shrink-0 cursor-pointer transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: '',
        success: '',
      },
      size: {
        sm: 'h-5 w-9',
        md: 'h-6 w-11',
        lg: 'h-7 w-13',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

const switchThumbVariants = cva(
  'pointer-events-none inline-block rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out',
  {
    variants: {
      size: {
        sm: 'h-3 w-3 data-[checked]:translate-x-4',
        md: 'h-4 w-4 data-[checked]:translate-x-5',
        lg: 'h-5 w-5 data-[checked]:translate-x-6',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof switchVariants> {
  label?: string;
  description?: string;
}

/**
 * Switch Component
 *
 * @example
 * ```tsx
 * <Switch />
 * <Switch checked={checked} onChange={(e) => setChecked(e.target.checked)} />
 * <Switch label="Notifications" description="Enable notifications" />
 * ```
 */
const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, variant, size, label, description, id, checked, ...props }, ref) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={ref}
            id={switchId}
            type="checkbox"
            className={cn(
              switchVariants({ variant, size }),
              'sr-only', // Hide the checkbox but keep it accessible
              'peer-checked:bg-primary-500',
              variant === 'success' && 'peer-checked:bg-success-500'
            )}
            checked={checked}
            {...props}
          />
          <div
            className={cn(
              switchVariants({ variant, size }),
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',
              'bg-neutral-200 peer-checked:bg-primary-500',
              variant === 'success' && 'peer-checked:bg-success-500'
            )}
            aria-hidden="true"
          >
            <div className={cn(switchThumbVariants({ size }))} aria-hidden="true" />
          </div>
        </div>

        {(label || description) && (
          <div className={cn('ml-3', label ? 'space-y-0.5' : '')}>
            {label && (
              <label
                htmlFor={switchId}
                className={cn(
                  'text-sm font-medium leading-none cursor-pointer',
                  props.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-xs text-neutral-500">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
